import { generatePuzzle, type ChemDokuPuzzle, type ChemDokuSize, type Difficulty } from "./engine";
import { PACKS } from "./packs";
import type { ChemDokuPack } from "./engine";

/**
 * Chem-doku Puzzle of the Day.
 *
 * Same puzzle for every visitor on the same local calendar day. Size,
 * pack, difficulty, and the generation seed are all derived from
 * `YYYY-MM-DD` so two students in different cities can compare results.
 * One puzzle per day; partial progress auto-saves so you can come back
 * and finish later.
 */

// ---------- date + hash ----------

export function todayKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// ---------- daily config ----------

/**
 * Difficulty by day-of-week so the week has a rhythm:
 * Mon easy, Tue/Thu/Sat medium, Wed/Fri hard, Sun easy. Size alternates
 * 4x4 on Mon to ease people back in, 6x6 the rest of the week.
 */
function dailyShape(date: Date): { size: ChemDokuSize; difficulty: Difficulty } {
  const dow = date.getDay(); // 0 = Sun
  const size: ChemDokuSize = dow === 1 ? 4 : 6;
  const difficulty: Difficulty =
    dow === 1 || dow === 0
      ? "easy"
      : dow === 3 || dow === 5
        ? "hard"
        : "medium";
  return { size, difficulty };
}

export interface DailyConfig {
  dateKey: string;
  size: ChemDokuSize;
  difficulty: Difficulty;
  pack: ChemDokuPack;
  seed: string;
}

export function getDailyConfig(date: Date = new Date()): DailyConfig {
  const dateKey = todayKey(date);
  const { size, difficulty } = dailyShape(date);
  const packs = PACKS[size];
  const pack = packs[hashString(`chemdoku-pack:${dateKey}`) % packs.length];
  const seed = `chemdoku-daily:${dateKey}:${size}:${difficulty}:${pack.id}`;
  return { dateKey, size, difficulty, pack, seed };
}

export function getDailyPuzzle(date: Date = new Date()): {
  config: DailyConfig;
  puzzle: ChemDokuPuzzle;
} {
  const config = getDailyConfig(date);
  const puzzle = generatePuzzle({
    size: config.size,
    difficulty: config.difficulty,
    pack: config.pack,
    seed: config.seed,
  });
  return { config, puzzle };
}

// ---------- persistence ----------

const PROGRESS_PREFIX = "elementpad:chemdoku-daily:progress:";
const RESULT_PREFIX = "elementpad:chemdoku-daily:result:";
const STREAK_KEY = "elementpad:chemdoku-daily:streak";
const LAST_PLAYED_KEY = "elementpad:chemdoku-daily:lastPlayed";

export interface DailyProgress {
  dateKey: string;
  board: (string | null)[][];
  moves: number;
}

export interface DailyResult {
  dateKey: string;
  moves: number;
  /** Time-to-solve in seconds, if measured. */
  seconds?: number;
  completedAt: string; // ISO timestamp
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function loadProgress(dateKey: string): DailyProgress | null {
  const ls = safeStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(PROGRESS_PREFIX + dateKey);
    return raw ? (JSON.parse(raw) as DailyProgress) : null;
  } catch {
    return null;
  }
}

export function saveProgress(p: DailyProgress): void {
  const ls = safeStorage();
  if (!ls) return;
  try {
    ls.setItem(PROGRESS_PREFIX + p.dateKey, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

export function loadResult(dateKey: string): DailyResult | null {
  const ls = safeStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(RESULT_PREFIX + dateKey);
    return raw ? (JSON.parse(raw) as DailyResult) : null;
  } catch {
    return null;
  }
}

export function saveResult(result: DailyResult): { current: number; best: number } {
  const ls = safeStorage();
  if (!ls) return { current: 0, best: 0 };
  try {
    ls.setItem(RESULT_PREFIX + result.dateKey, JSON.stringify(result));

    const lastPlayed = ls.getItem(LAST_PLAYED_KEY);
    const prevRaw = ls.getItem(STREAK_KEY);
    const prev = prevRaw ? JSON.parse(prevRaw) : { current: 0, best: 0 };
    const prevStreak = parseInt(String(prev.current ?? 0), 10) || 0;
    const prevBest = parseInt(String(prev.best ?? 0), 10) || 0;

    let nextStreak = 1;
    if (lastPlayed === result.dateKey) {
      nextStreak = prevStreak || 1;
    } else if (lastPlayed) {
      const last = new Date(lastPlayed + "T00:00:00");
      const today = new Date(result.dateKey + "T00:00:00");
      const diffDays = Math.round((today.getTime() - last.getTime()) / 86400000);
      nextStreak = diffDays === 1 ? prevStreak + 1 : 1;
    }

    const nextBest = Math.max(prevBest, nextStreak);
    ls.setItem(LAST_PLAYED_KEY, result.dateKey);
    ls.setItem(STREAK_KEY, JSON.stringify({ current: nextStreak, best: nextBest }));
    return { current: nextStreak, best: nextBest };
  } catch {
    return { current: 0, best: 0 };
  }
}

export function loadStreak(): { current: number; best: number } {
  const ls = safeStorage();
  if (!ls) return { current: 0, best: 0 };
  try {
    const raw = ls.getItem(STREAK_KEY);
    if (!raw) return { current: 0, best: 0 };
    const parsed = JSON.parse(raw);
    const current = parseInt(String(parsed.current ?? 0), 10) || 0;
    const best = parseInt(String(parsed.best ?? 0), 10) || 0;

    const lastPlayed = ls.getItem(LAST_PLAYED_KEY);
    if (lastPlayed) {
      const last = new Date(lastPlayed + "T00:00:00");
      const today = new Date(todayKey() + "T00:00:00");
      const diffDays = Math.round((today.getTime() - last.getTime()) / 86400000);
      if (diffDays > 1) return { current: 0, best };
    }
    return { current, best };
  } catch {
    return { current: 0, best: 0 };
  }
}

// ---------- share text ----------

function formatDuration(seconds?: number): string {
  if (!seconds || seconds < 0) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m${s.toString().padStart(2, "0")}s` : `${s}s`;
}

export function buildShareText(config: DailyConfig, result: DailyResult): string {
  const time = formatDuration(result.seconds);
  return [
    `Chem-doku Daily ${config.dateKey}`,
    `${config.size}x${config.size} ${config.difficulty} - ${config.pack.label}`,
    `Solved in ${result.moves} moves${time ? ` (${time})` : ""}`,
    "🧪🟦🧪",
    "https://elementpad.net/play/chem-doku",
  ].join("\n");
}